import { useState } from 'react'
import { usePage } from '@inertiajs/react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import { splitRruleTime, humanReadableStructuralRrule } from '@/shared/lib/frequency/rrule-utils'
import { formatWallClockTime, type TenantTimeFormatInput } from '@/shared/ui/wall-clock-input-utils'
import { t } from '@/shared/lib/i18n'
import type { SharedProps } from '@/shared/types/SharedProps'

const TIME_UNIT_TO_MINUTES: Record<string, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
  months: 43200,
  years: 525600,
}

function formatDuration(
  minutes: number | null | undefined,
  unit: string | null | undefined,
): string {
  if (minutes == null || !unit) return '—'
  const rate = TIME_UNIT_TO_MINUTES[unit] ?? 1
  const value = minutes / rate
  const display = value === Math.floor(value) ? String(value) : value.toPrecision(3)
  return `${display} ${unit}`
}

export interface FrequencyContextProps {
  /** e.g. "TIMED", "CALENDAR_FIXED", "UNSCHEDULED" — null when unknown */
  frequencyTypeCode: string | null | undefined
  /** Human-readable base description from intervalTypeDescription */
  intervalTypeDescription: string | null | undefined
  /** Raw RRULE string (may include BYHOUR/BYMINUTE) */
  frequencyRrule: string | null | undefined
  leadTimeIntervalHours: number | null | undefined
  leadTimeUnit: string | null | undefined
  lateTimeIntervalHours: number | null | undefined
  lateTimeUnit: string | null | undefined
  /** Task-level override for start time (HH:MM) */
  fixedScheduleStartTime: string | null | undefined
  /** Whether the overdue window is capped at 23:59:59 of the due day */
  endsAtMidnight: boolean | null | undefined
  /** Omit intervalTypeDescription from the collapsed summary when the caller already renders it. */
  hideDescriptionInSummary?: boolean
  /** Override shared `tenantTimeFormat` when the caller already has a resolved value. */
  tenantTimeFormat?: TenantTimeFormatInput
}

const FREQUENCY_TYPE_LABELS: Record<string, string> = {
  TIMED: 'Timed',
  CALENDAR_FIXED: 'Calendar',
  UNSCHEDULED: 'Unscheduled',
}

/**
 * Inline-expandable frequency detail panel.
 * Collapsed: one-line summary. Expanded: structured definition table.
 */
export function FrequencyContextPanel({
  frequencyTypeCode,
  intervalTypeDescription,
  frequencyRrule,
  leadTimeIntervalHours,
  leadTimeUnit,
  lateTimeIntervalHours,
  lateTimeUnit,
  fixedScheduleStartTime,
  endsAtMidnight,
  hideDescriptionInSummary = false,
  tenantTimeFormat: tenantTimeFormatProp,
}: FrequencyContextProps) {
  const [open, setOpen] = useState(false)
  const { tenantTimeFormat: sharedTenantTimeFormat } = usePage<SharedProps>().props
  const tenantTimeFormat = tenantTimeFormatProp ?? sharedTenantTimeFormat

  if (!intervalTypeDescription && !frequencyTypeCode) return null

  const isCalendar = frequencyTypeCode === 'CALENDAR_FIXED'
  const isTimed = frequencyTypeCode === 'TIMED'

  const { structuralRrule, startsAt: rruleStartsAt } = splitRruleTime(frequencyRrule ?? '')
  const pattern = structuralRrule ? humanReadableStructuralRrule(structuralRrule) : null
  // Task-level start time overrides RRULE time; fall back to RRULE time for display.
  const rawStartTime = fixedScheduleStartTime || rruleStartsAt || null
  const displayStartTime = rawStartTime ? formatWallClockTime(rawStartTime, tenantTimeFormat) : null

  const typeLabel = frequencyTypeCode
    ? (FREQUENCY_TYPE_LABELS[frequencyTypeCode] ?? frequencyTypeCode)
    : null

  // Suppress the description when it merely echoes the type label (e.g. an UNSCHEDULED task
  // whose interval description is also "Unscheduled") so the summary never reads
  // "Unscheduled · Unscheduled". Comparison is case-insensitive and trimmed.
  const description = hideDescriptionInSummary ? null : (intervalTypeDescription ?? pattern)
  const descriptionEchoesType =
    typeLabel != null &&
    description != null &&
    typeLabel.trim().toLowerCase() === description.trim().toLowerCase()

  const summary = [
    typeLabel,
    descriptionEchoesType ? null : description,
    isCalendar && displayStartTime
      ? `${t('admin.instrumentTypes.maintenanceModal.startsAtLabel')} ${displayStartTime}`
      : null,
    isTimed && leadTimeIntervalHours != null
      ? `${t('admin.instrumentTypes.maintenanceModal.leadTimeLabel')} ${formatDuration(leadTimeIntervalHours, leadTimeUnit)}`
      : null,
    lateTimeIntervalHours != null
      ? `${t('instruments.tasksSchedule.taskForm.overdue')} ${formatDuration(lateTimeIntervalHours, lateTimeUnit)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const hasDetails = pattern || displayStartTime || leadTimeIntervalHours || lateTimeIntervalHours

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-left text-xs text-muted-foreground hover:text-foreground transition-colors"
          aria-expanded={open}
          onClick={(event) => event.stopPropagation()}
        >
          {open ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )}
          <span>{summary || t('instruments.tasksSchedule.taskForm.intervalType')}</span>
        </button>
      </CollapsibleTrigger>

      {hasDetails && (
        <CollapsibleContent>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 pl-4 text-xs">
            {typeLabel && (
              <>
                <dt className="text-muted-foreground">
                  {t('admin.instrumentTypes.planning.taskFrequencyTypeLabel')}
                </dt>
                <dd>{typeLabel}</dd>
              </>
            )}
            {pattern && (
              <>
                <dt className="text-muted-foreground">
                  {t('instruments.tasksSchedule.taskForm.pattern')}
                </dt>
                <dd>{pattern}</dd>
              </>
            )}
            {isCalendar && displayStartTime && (
              <>
                <dt className="text-muted-foreground">
                  {t('admin.instrumentTypes.maintenanceModal.startsAtLabel')}
                </dt>
                <dd>{displayStartTime}</dd>
              </>
            )}
            {isTimed && leadTimeIntervalHours != null && (
              <>
                <dt className="text-muted-foreground">
                  {t('admin.instrumentTypes.maintenanceModal.leadTimeLabel')}
                </dt>
                <dd>{formatDuration(leadTimeIntervalHours, leadTimeUnit)}</dd>
              </>
            )}
            {lateTimeIntervalHours != null && (
              <>
                <dt className="text-muted-foreground">
                  {t('instruments.tasksSchedule.taskForm.overdue')}
                </dt>
                <dd>{formatDuration(lateTimeIntervalHours, lateTimeUnit)}</dd>
              </>
            )}
            {isCalendar && endsAtMidnight != null && (
              <>
                <dt className="text-muted-foreground">
                  {t('instruments.tasksSchedule.form.rrule.endsAtMidnight')}
                </dt>
                <dd>
                  {endsAtMidnight
                    ? t('instruments.tasksSchedule.yes')
                    : t('instruments.tasksSchedule.no')}
                </dd>
              </>
            )}
          </dl>
        </CollapsibleContent>
      )}
    </Collapsible>
  )
}
